import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { fetchTrailerListingById, createTrailerListing, updateTrailerListing } from '../../services/trailerListingService';
import MainNavigation from '../../components/ui/MainNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SpecificationsForm from './components/SpecificationsForm';
import PhotoUpload from './components/PhotoUpload';
import PricingForm from './components/PricingForm';
import AvailabilitySection from './components/AvailabilitySection';

const TrailerListingCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [listingId, setListingId] = useState(null);

  const [formData, setFormData] = useState({
    specifications: {
      trailerType: '',
      length: '',
      width: '',
      height: '',
      weightCapacity: '',
      hitchSize: '',
      features: [],
      year: '',
      make: '',
      model: '',
      vin: '',
      licensePlate: ''
    },
    location: {
      address: '',
      city: '',
      state: '',
      zip: '',
      latitude: '',
      longitude: ''
    },
    photos: [],
    pricing: {
      dailyRate: '',
      weeklyRate: '',
      monthlyRate: '',
      deliveryFee: '',
      cleaningFee: '',
      securityDeposit: ''
    },
    availability: [],
    pickupTime: '',
    dropoffTime: ''
  });

  useEffect(() => {
    const loadExistingListing = async () => {
      const stateListingId = location?.state?.listingId;
      if (stateListingId) {
        setIsLoading(true);
        setEditMode(true);
        setListingId(stateListingId);
        try {
          const listing = await fetchTrailerListingById(stateListingId);
          if (listing) {
            setFormData({
              specifications: {
                trailerType: listing?.trailerType || '',
                length: listing?.lengthFeet?.toString() || '',
                width: listing?.widthFeet?.toString() || '',
                height: listing?.heightFeet?.toString() || '',
                weightCapacity: listing?.weightCapacityLbs?.toString() || '',
                hitchSize: listing?.hitchSize || '',
                features: listing?.features || [],
                year: listing?.year?.toString() || '',
                make: listing?.make || '',
                model: listing?.model || '',
                vin: listing?.vin || '',
                licensePlate: listing?.licensePlate || ''
              },
              location: {
                address: listing?.locationAddress || '',
                city: listing?.locationCity || '',
                state: listing?.locationState || '',
                zip: listing?.locationZip || '',
                latitude: listing?.locationLatitude?.toString() || '',
                longitude: listing?.locationLongitude?.toString() || ''
              },
              photos: listing?.photos?.map((url, index) => ({
                id: `existing-${index}`,
                preview: url,
                url: url,
                isExisting: true
              })) || [],
              pricing: {
                dailyRate: listing?.dailyRate?.toString() || '',
                weeklyRate: listing?.weeklyRate?.toString() || '',
                monthlyRate: listing?.monthlyRate?.toString() || '',
                deliveryFee: listing?.deliveryFee?.toString() || '',
                cleaningFee: listing?.cleaningFee?.toString() || '',
                securityDeposit: listing?.securityDeposit?.toString() || ''
              },
              availability: listing?.availabilityDates || [],
              pickupTime: listing?.pickupTime || '',
              dropoffTime: listing?.dropoffTime || ''
            });
          }
        } catch (err) {
          setError('Failed to load listing data');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadExistingListing();
  }, [location?.state?.listingId]);

  const steps = [
    { number: 1, title: 'Specifications', icon: 'FileText' },
    { number: 2, title: 'Location', icon: 'MapPin' },
    { number: 3, title: 'Photos', icon: 'Image' },
    { number: 4, title: 'Pricing', icon: 'DollarSign' },
    { number: 5, title: 'Availability', icon: 'Calendar' }
  ];

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePublish = async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to publish a listing');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const listingData = {
        owner_id: user?.id,
        title: `${formData?.specifications?.year || ''} ${formData?.specifications?.make || ''} ${formData?.specifications?.trailerType} Trailer`?.trim(),
        description: `${formData?.specifications?.length} x ${formData?.specifications?.width} ${formData?.specifications?.trailerType} trailer`,
        trailer_type: formData?.specifications?.trailerType,
        length_feet: parseFloat(formData?.specifications?.length) || null,
        width_feet: parseFloat(formData?.specifications?.width) || null,
        height_feet: parseFloat(formData?.specifications?.height) || null,
        weight_capacity_lbs: parseInt(formData?.specifications?.weightCapacity) || null,
        hitch_size: formData?.specifications?.hitchSize || null,
        year: parseInt(formData?.specifications?.year) || null,
        make: formData?.specifications?.make || null,
        model: formData?.specifications?.model || null,
        vin: formData?.specifications?.vin || null,
        license_plate: formData?.specifications?.licensePlate || null,
        features: formData?.specifications?.features,
        location_address: formData?.location?.address || null,
        location_city: formData?.location?.city,
        location_state: formData?.location?.state,
        location_zip: formData?.location?.zip || null,
        location_latitude: parseFloat(formData?.location?.latitude) || null,
        location_longitude: parseFloat(formData?.location?.longitude) || null,
        daily_rate: parseFloat(formData?.pricing?.dailyRate) || 0,
        weekly_rate: parseFloat(formData?.pricing?.weeklyRate) || null,
        monthly_rate: parseFloat(formData?.pricing?.monthlyRate) || null,
        delivery_fee: parseFloat(formData?.pricing?.deliveryFee) || 0,
        cleaning_fee: parseFloat(formData?.pricing?.cleaningFee) || 0,
        security_deposit: parseFloat(formData?.pricing?.securityDeposit) || 0,
        pickup_time: formData?.pickupTime || null,
        dropoff_time: formData?.dropoffTime || null,
        availability_dates: formData?.availability,
        photos: formData?.photos?.map(p => p?.url || p?.preview),
        status: 'active'
      };

      if (editMode && listingId) {
        await updateTrailerListing(listingId, listingData);
        navigate('/owner-dashboard', { state: { message: 'Trailer listing updated successfully!' } });
      } else {
        await createTrailerListing(listingData);
        navigate('/owner-dashboard', { state: { message: 'Trailer listing published successfully!' } });
      }
    } catch (err) {
      setError(err?.message || 'Failed to publish listing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="font-body text-muted-foreground">Loading listing data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              {editMode ? 'Edit Your Trailer Listing' : 'List Your Trailer'}
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground">
              {editMode ? 'Update your trailer information' : 'Share your trailer with the community and start earning'}
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-smooth ${
                        currentStep >= step?.number
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {currentStep > step?.number ? (
                        <Icon name="Check" size={20} strokeWidth={2.5} />
                      ) : (
                        <Icon name={step?.icon} size={20} strokeWidth={2} />
                      )}
                    </div>
                    <span className="mt-2 text-xs md:text-sm font-medium text-foreground hidden sm:block">
                      {step?.title}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded transition-smooth ${
                        currentStep > step?.number ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8">
            {currentStep === 1 && (
              <SpecificationsForm
                data={formData?.specifications}
                onChange={(data) => updateFormData('specifications', data)}
              />
            )}

            {currentStep === 2 && (
              <LocationForm
                data={formData?.location}
                onChange={(data) => updateFormData('location', data)}
              />
            )}

            {currentStep === 3 && (
              <PhotoUpload
                photos={formData?.photos}
                onChange={(photos) => updateFormData('photos', photos)}
              />
            )}

            {currentStep === 4 && (
              <PricingForm
                data={formData?.pricing}
                onChange={(data) => updateFormData('pricing', data)}
              />
            )}

            {currentStep === 5 && (
              <AvailabilitySection
                availability={formData?.availability}
                pickupTime={formData?.pickupTime}
                dropoffTime={formData?.dropoffTime}
                onChange={(dates) => updateFormData('availability', dates)}
                onPickupTimeChange={(time) => setFormData(prev => ({ ...prev, pickupTime: time }))}
                onDropoffTimeChange={(time) => setFormData(prev => ({ ...prev, dropoffTime: time }))}
              />
            )}

            {error && (
              <div className="mt-6 p-4 bg-error/10 border border-error rounded-md flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                <p className="font-body text-sm text-error">{error}</p>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1 || isSubmitting}
                iconName="ChevronLeft"
              >
                Back
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handlePublish}
                  loading={isSubmitting}
                  iconName="Upload"
                >
                  {editMode ? 'Update Listing' : 'Publish Listing'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LocationForm = ({ data, onChange }) => {
  const handleInputChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
          Location Details
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Where is your trailer located?
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Street Address"
          type="text"
          placeholder="123 Main Street"
          value={data?.address}
          onChange={(e) => handleInputChange('address', e?.target?.value)}
          description="Full street address"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="Austin"
            value={data?.city}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            required
          />
          <Input
            label="State"
            type="text"
            placeholder="TX"
            value={data?.state}
            onChange={(e) => handleInputChange('state', e?.target?.value)}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ZIP Code"
            type="text"
            placeholder="78701"
            value={data?.zip}
            onChange={(e) => handleInputChange('zip', e?.target?.value)}
          />
        </div>
      </div>
    </div>
  );
};

import Input from '../../components/ui/Input';

export default TrailerListingCreation;